import { useMemo } from 'react';
import { DATE_MASK_FORMAT, DATE_MASK_WITHOUT_YEAR_FORMAT, maskPhone } from '@givebutter/util';
import { ContactFormValues, EmailItem, PhoneItem } from 'assets/js/pages/contacts/types';
import { createFields, toFormValues } from 'components/custom-fields/helpers';
import { SelectItemWithLabel } from 'components/select';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useSuspenseCurrentAccountTags } from 'customHooks/useCurrentAccountTags';
import { useCurrentPossibleContact } from 'customHooks/useCurrentContact';
import { useQueryParams } from 'customHooks/useQueryParams';
import { dateOfBirthToFormat } from 'helpers/dates';
import { Contact } from 'types/domain';

const useDefaultContactFormValues = (additionalValues?: Partial<ContactFormValues>) => {
    const { customFields } = useCurrentAccount();
    const queryParams = useQueryParams<{ type: ContactType }>();

    const contactCustomFields = useMemo(() => {
        return customFields?.contact || [];
    }, [customFields]);

    const { data: contact } = useCurrentPossibleContact();
    const { data: accountContactTags } = useSuspenseCurrentAccountTags();

    const values: ContactFormValues = useMemo(() => {
        const hasEmails = contact?.emails && contact.emails.length > 0;
        const hasPhones = contact?.phones && contact.phones.length > 0;
        const hasAddresses = contact?.addresses && contact.addresses.length > 0;
        const hasCustomFields = contact?.custom_fields && contact.custom_fields.length > 0;

        const defaultEmails = getDefaultEmails(contact?.type);
        const defaultPhones = getDefaultPhones(contact?.type);

        const persistedTags = contact?.tags ?? [];
        const tags: SelectItemWithLabel[] = accountContactTags
            .filter(tag => persistedTags.indexOf(tag.name) > -1)
            .map(tag => ({ id: String(tag.id), label: tag.name }));

        const emails = hasEmails
            ? contact.emails.map(item => ({
                  isPrimary: item.value === contact.primary_email,
                  value: item.value,
                  type: item.type,
              }))
            : defaultEmails;
        const phones = hasPhones
            ? contact.phones.map(item => ({
                  isPrimary: item.value === contact.primary_phone,
                  value: maskPhone(item.value),
                  type: item.type,
              }))
            : defaultPhones;

        const associatedCompany = contact?.associated_companies?.[0];

        const defaultDob = dateOfBirthToFormat(contact?.dob ?? '', {
            long: DATE_MASK_FORMAT,
            short: DATE_MASK_WITHOUT_YEAR_FORMAT,
        });

        return {
            selectedContact: [],
            selectedCompany: associatedCompany
                ? [
                      {
                          id: `${associatedCompany.id}`,
                          label: associatedCompany.company_name ?? 'N/A',
                          value: associatedCompany as Contact,
                      },
                  ]
                : [],
            companyName: contact?.company_name ?? '',
            employer: contact?.employer ?? '',
            company: contact?.company ?? '',
            type: contact?.type ?? queryParams.type ?? ContactType.Individual,
            firstName: contact?.first_name ?? '',
            firstNamePrefix: contact?.prefix ?? '',
            middleName: contact?.middle_name ?? '',
            lastName: contact?.last_name ?? '',
            lastNameSuffix: contact?.suffix ?? '',
            dob: defaultDob,
            gender: contact?.gender ?? 'select',
            allowEngageEmail: contact?.is_email_subscribed ?? true,
            allowEngageSms: contact?.is_phone_subscribed ?? true,
            allowEngageAddress: contact?.is_address_subscribed ?? true,
            didConfirmEngage:
                (contact?.is_email_subscribed || contact?.is_phone_subscribed) ?? false,
            legacyTitle: contact?.title ?? '',
            websiteUrl: contact?.website_url ?? '',
            linkedInUrl: contact?.linkedin_url ?? '',
            twitterUrl: contact?.twitter_url ?? '',
            facebookUrl: contact?.facebook_url ?? '',
            note: contact?.note ?? '',
            tags,
            addresses: hasAddresses
                ? contact.addresses.map(address => {
                      return {
                          id: address?.id.toString(),
                          line1: address?.address_1 ?? '',
                          line2: address?.address_2 ?? '',
                          city: address?.city ?? '',
                          zip: address?.zipcode ?? '',
                          stateUs: address?.country === 'USA' ? (address?.state ?? '') : '',
                          stateIntl: address?.country !== 'USA' ? (address?.state ?? '') : '',
                          country: address?.country ?? 'USA',
                          isPrimary: address?.is_primary ?? false,
                      };
                  })
                : [
                      {
                          line1: '',
                          line2: '',
                          city: '',
                          zip: '',
                          stateUs: '',
                          stateIntl: '',
                          country: 'USA',
                          isPrimary: true,
                      },
                  ],
            customFields: hasCustomFields
                ? toFormValues(contact.custom_fields) // responses from the edited contact
                : toFormValues(createFields(contactCustomFields)), // account custom fields from the contact group
            ui: {
                showPrefix: !!(contact?.prefix ?? false),
                showSuffix: !!(contact?.suffix ?? false),
            },
            emails,
            phones,
            companyPhones:
                contact?.type === ContactType.Company
                    ? phones
                    : [{ value: '', isPrimary: true, type: 'work' }],
            companyEmails:
                contact?.type === ContactType.Company
                    ? emails
                    : [{ value: '', isPrimary: true, type: 'work' }],
            pointOfContactPhones: [{ value: '', isPrimary: true, type: 'work' }],
            pointOfContactEmails: [{ value: '', isPrimary: true, type: 'work' }],
            ...additionalValues,
        };
    }, [contact, accountContactTags, contactCustomFields, queryParams.type, additionalValues]);

    return values;
};

const getDefaultEmails = (type: ContactType = ContactType.Individual): EmailItem[] => {
    return [
        {
            value: '',
            isPrimary: true,
            type: type === ContactType.Company ? 'work' : 'personal',
        },
    ];
};

const getDefaultPhones = (type: ContactType = ContactType.Individual): PhoneItem[] => {
    return [
        {
            value: '',
            isPrimary: true,
            type: type === ContactType.Company ? 'work' : 'home',
        },
    ];
};

export { useDefaultContactFormValues };
